/**
 * Datos de api-taquilla: inventario, carrito, órdenes, pagos, QR.
 *
 * El JWT del cliente (emitido por Core) es validado por Taquilla con el
 * mismo secreto, así que el mismo token sirve. El cliente HTTP adjunta el
 * Bearer automáticamente.
 */

import { API_TAQUILLA_URL } from './config';
import { apiRequest, apiRequestWithMeta } from './http';

// ---- Inventario -----------------------------------------------------------

export interface InventoryStageDto {
  stage_id: string;
  label?: string | null;
  capacity_total?: number | null;
  capacity_available?: number | null;
  price_full_cents?: number | string | null;
  price_promo_cents?: number | string | null;
  status?: string | null;
  updated_at?: string | null;
}

/** GET /v1/events/:id/inventory → lista de etapas con cupo y precio. */
export async function getInventory(eventId: string): Promise<InventoryStageDto[]> {
  const data = await apiRequest<InventoryStageDto[]>(
    API_TAQUILLA_URL,
    `/events/${encodeURIComponent(eventId)}/inventory`,
  );
  return data ?? [];
}

// ---- Localidades ----------------------------------------------------------

export interface LocalityDto {
  id: string;
  event_id?: string | null;
  name?: string | null;
  status?: string | null;
  capacity?: number | null;
  sold?: number | null;
  reserved?: number | null;
  /** Precio en pesos COP enteros (NO centavos), tal cual lo expone el server. */
  price_full?: number | string | null;
  price_promo?: number | string | null;
  [key: string]: unknown;
}

/**
 * GET /v1/events/:id/localities → localidades vendibles con su precio real.
 *
 * El endpoint de `inventory` modela "etapas" cuyo `price_*_cents` suele venir
 * en 0 para eventos sin pricing por etapa migrado; las localidades sí traen el
 * precio real (`price_full`/`price_promo` en pesos enteros). Se usa como
 * fuente de precio de respaldo para que el SeatPicker nunca muestre COP 0.
 */
export async function getLocalities(eventId: string): Promise<LocalityDto[]> {
  const data = await apiRequest<LocalityDto[]>(
    API_TAQUILLA_URL,
    `/events/${encodeURIComponent(eventId)}/localities`,
  );
  return data ?? [];
}

// ---- Mapa de asientos (compra por silla) ----------------------------------

export interface SeatMapSeatDto {
  id: string;
  label?: string | null;
  row?: string | null; // F5 iter2 — fila (A, B, …)
  number?: string | null; // F5 iter2 — columna dentro de la fila (1, 2, …)
  x: number;
  y: number;
  status: string; // available | reserved | sold | blocked
  bleacher?: boolean; // silla de gradería
  /**
   * Id de grupo mesa/palco/gradería: `mesa:gN` / `palco:gN` / `grad:gN`, con
   * sufijo opcional `:r{deg}` (rotación). El backend ya lo expone (metadata.g);
   * el lienzo lo usa para dibujar mesas/palcos como UN contorno (no sillas sueltas).
   */
  group?: string | null;
}

export interface SeatMapLocalityDto {
  id: string;
  stage_id?: string | null;
  name: string;
  price_cop?: number | null;
  capacity: number;
  available: number;
  color_hex?: string | null;
  bleacher?: boolean; // localidad-gradería (render con líneas + marco)
  rows?: number; // nº de filas detectadas/asignadas
  polygon_points: number[][];
  seats: SeatMapSeatDto[];
}

export interface SeatMapDto {
  event_id: string;
  viewbox: { width: number; height: number };
  reference_areas: Array<{
    name?: string | null;
    polygon_points?: number[][];
    label_xy?: { x: number; y: number };
  }>;
  localities: SeatMapLocalityDto[];
}

/** GET /v1/events/:id/seat-map → geometría + estado por silla + precio/localidad. */
export async function getSeatMap(eventId: string): Promise<SeatMapDto> {
  return apiRequest<SeatMapDto>(
    API_TAQUILLA_URL,
    `/events/${encodeURIComponent(eventId)}/seat-map`,
  );
}

// ---- Carrito --------------------------------------------------------------

export interface CartDto {
  id: string;
  event_id?: string | null;
  status?: string | null;
  expires_at?: string | null;
  [key: string]: unknown;
}

export interface CartItemInput {
  stage_id: string;
  quantity: number;
  // Compra por silla (F5): asientos puntuales + titular de cada uno (mismo orden
  // que seat_ids). Ausentes en cupo/admisión general.
  locality_id?: string | null;
  seat_ids?: string[];
  holder_names?: string[];
}

/** POST /v1/cart — crea un carrito para un evento. */
export async function createCart(eventId: string): Promise<CartDto> {
  return apiRequest<CartDto>(API_TAQUILLA_URL, '/cart', {
    method: 'POST',
    body: { event_id: eventId },
  });
}

/** POST /v1/cart/:id/items — agrega líneas al carrito. */
export async function addCartItems(cartId: string, items: CartItemInput[]): Promise<CartDto> {
  return apiRequest<CartDto>(API_TAQUILLA_URL, `/cart/${encodeURIComponent(cartId)}/items`, {
    method: 'POST',
    body: { items },
  });
}

// ---- Órdenes --------------------------------------------------------------

export interface OrderItemDto {
  id: string;
  item_type?: string | null;
  stage_id?: string | null;
  locality_id?: string | null;
  quantity?: number | null;
  unit_price_cents?: number | null;
  subtotal_cents?: number | null;
}

export interface QrCodeDto {
  id: string;
  order_id?: string | null;
  order_item_id?: string | null;
  code: string;
  is_active?: boolean;
  expires_at?: string | null;
  used_at?: string | null;
  // QR nominal por silla (F5): asiento + titular. NULL para cupo/general.
  seat_id?: string | null;
  seat_label?: string | null;
  holder_name?: string | null;
  [key: string]: unknown;
}

export interface OrderDto {
  id: string;
  order_number?: string | null;
  status: string;
  event_id?: string | null;
  cart_id?: string | null;
  subtotal_cents?: number | null;
  fees_cents?: number | null;
  total_cents?: number | null;
  currency?: string | null;
  created_at?: string | null;
  confirmed_at?: string | null;
  items?: OrderItemDto[];
  qr_codes?: QrCodeDto[];
}

/** POST /v1/orders — convierte un carrito en orden. */
export async function createOrder(cartId: string, idempotencyKey?: string): Promise<OrderDto> {
  return apiRequest<OrderDto>(API_TAQUILLA_URL, '/orders', {
    method: 'POST',
    body: { cart_id: cartId },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/** GET /v1/orders/:id — detalle de la orden con items + QRs. */
export async function getOrder(orderId: string): Promise<OrderDto> {
  return apiRequest<OrderDto>(API_TAQUILLA_URL, `/orders/${encodeURIComponent(orderId)}`);
}

/** GET /v1/orders/mine — órdenes del usuario autenticado. */
export async function listMyOrders(): Promise<OrderDto[]> {
  const env = await apiRequestWithMeta<OrderDto[]>(API_TAQUILLA_URL, '/orders/mine');
  return env.data ?? [];
}

// ---- Pagos ----------------------------------------------------------------

export interface CheckoutResult {
  payment_id: string;
  gateway: string;
  gateway_payment_id?: string | null;
  redirect_url?: string | null;
  expires_at?: string | null;
}

/** POST /v1/payments/checkout — crea el intento de pago (ePayco/stub). */
export async function createCheckout(
  orderId: string,
  returnUrl: string,
  gateway = 'stub',
  idempotencyKey?: string,
): Promise<CheckoutResult> {
  return apiRequest<CheckoutResult>(API_TAQUILLA_URL, '/payments/checkout', {
    method: 'POST',
    body: { order_id: orderId, return_url: returnUrl, gateway },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

/**
 * POST /v1/payments/:id/confirm-stub — auto-confirmación del pago stub por el
 * comprador (GAP-01). Solo válida en entornos con gateway stub; avanza la orden
 * a confirmed/qr_issued reutilizando taquilla.confirm_payment. Idempotente.
 */
export async function confirmStubPayment(
  paymentId: string,
): Promise<{ payment_id: string; order_id: string; status: string; replay?: boolean }> {
  return apiRequest(API_TAQUILLA_URL, `/payments/${encodeURIComponent(paymentId)}/confirm-stub`, {
    method: 'POST',
    body: {},
  });
}

// ---- QR --------------------------------------------------------------------

/** GET /v1/qr/order/:id — QRs emitidos para una orden. */
export async function getOrderQrCodes(orderId: string): Promise<QrCodeDto[]> {
  const data = await apiRequest<QrCodeDto[]>(
    API_TAQUILLA_URL,
    `/qr/order/${encodeURIComponent(orderId)}`,
  );
  return data ?? [];
}

// ---- Lista de espera (SDD-029) --------------------------------------------

/** Estado del fan en la fila de una localidad agotada. */
export interface WaitlistStatusDto {
  enrolled: boolean;
  /** Posición 1-based; `null` si no está anotado. */
  position: number | null;
}

/** Resultado de anotarse: posición y turno asignados. */
export interface WaitlistEnrollDto {
  enrolled: boolean;
  position: number | null;
  ticket_number?: number | null;
}

/**
 * GET /v1/waitlist/status — posición del fan, o `enrolled:false`.
 * `localityId` NULL/omitido = la fila de «cualquier localidad del evento».
 */
export async function getWaitlistStatus(
  eventId: string,
  localityId?: string | null,
): Promise<WaitlistStatusDto> {
  return apiRequest<WaitlistStatusDto>(API_TAQUILLA_URL, '/waitlist/status', {
    query: { event_id: eventId, locality_id: localityId ?? undefined },
  });
}

/**
 * POST /v1/waitlist/enroll — anotarse. **409 si ya estaba anotado**: el
 * `ApiError` de ese caso trae la posición en `meta.position`.
 */
export async function enrollWaitlist(
  eventId: string,
  localityId?: string | null,
  qtyWanted = 1,
): Promise<WaitlistEnrollDto> {
  return apiRequest<WaitlistEnrollDto>(API_TAQUILLA_URL, '/waitlist/enroll', {
    method: 'POST',
    body: { event_id: eventId, locality_id: localityId ?? null, qty_wanted: qtyWanted },
  });
}

/** DELETE /v1/waitlist/cancel — baja voluntaria de la fila. */
export async function cancelWaitlist(
  eventId: string,
  localityId?: string | null,
): Promise<{ cancelled: boolean }> {
  return apiRequest(API_TAQUILLA_URL, '/waitlist/cancel', {
    method: 'DELETE',
    body: { event_id: eventId, locality_id: localityId ?? null },
  });
}
